import * as Sentry from "@sentry/react";
import {
	type UseMutationResult,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { useMemo } from "react";
import type {
	StepStatePayload,
	TaskProgressPayload,
} from "../domain/journeyProgress/schemas";
import {
	closeStep as closeStepRequest,
	completeTask as completeTaskRequest,
	getJourneyProgress,
	openStep as openStepRequest,
	reopenTask as reopenTaskRequest,
} from "../services/journeyProgressService";
import {
	getJourney,
	resolveJourneyContentLocale,
} from "../services/journeyService";
import type { Journey, JourneyType } from "../types/journey";
import type { JourneyContentLocale } from "../types/journeyS3Schema";

/**
 * M011/S01 T06: merges the immutable CDN journey JSON (FE-only catalog) with
 * the per-user progress payload (era-api /api/journey-progress) and exposes
 * idempotent mutations with optimistic + rollback semantics.
 *
 * Architecture invariants:
 * - Orphan filtering is client-side: taskIds present in the DB but absent
 *   from the current CDN journey JSON are dropped from `taskProgress` and
 *   `completedTaskIds`. The server never reads the CDN JSON, so this is the
 *   only place where catalog/progress reconciliation happens. (M011 boundary
 *   map decision: CDN JSON is FE-only.)
 * - Unsupported journeyType (CDN fetch fails / journey JSON missing) is NOT
 *   a thrown error — the hook returns `journey: null` with a populated
 *   `error`. S02 (Get Ready) consumes `journey: null` as the empty-state
 *   signal rather than crashing the page.
 * - Mutations use the canonical TanStack Query v5 optimistic-write pattern:
 *   `onMutate` snapshots the cache and patches it, `onError` rolls back to
 *   the snapshot, `onSettled` invalidates so the next render sees the
 *   server-confirmed state. Snapshot is whole-payload — the mutation
 *   responses (`taskProgress` or `stepState`) get spliced into a fresh copy.
 */

export type StepState = StepStatePayload;
export type TaskProgress = TaskProgressPayload;

export interface JourneyProgressMutations {
	completeTask: UseMutationResult<
		TaskProgress,
		Error,
		{ taskId: string; progressPercent?: number }
	>;
	reopenTask: UseMutationResult<TaskProgress, Error, { taskId: string }>;
	openStep: UseMutationResult<StepState, Error, { stepId: string }>;
	closeStep: UseMutationResult<StepState, Error, { stepId: string }>;
}

export interface UseJourneyProgressResult {
	journey: Journey | null;
	journeyStartedAt: string | null;
	stepStates: Map<string, StepState>;
	taskProgress: Map<string, TaskProgress>;
	completedTaskIds: Set<string>;
	isLoading: boolean;
	error: Error | null;
	mutations: JourneyProgressMutations;
}

interface MergedPayload {
	journey: Journey | null;
	journeyStartedAt: string | null;
	stepStates: StepState[];
	taskProgress: TaskProgress[];
	completedTaskIds: string[];
}

/**
 * The key carries the *content* locale, not the raw app locale: i18n boots at the
 * default locale and only switches to the user's stored language afterwards, so a
 * locale-less key would keep serving the English payload fetched during that first
 * render. Keying on the resolved content locale also means locales without CDN
 * content (fr, ru, …) collapse onto the single `en` cache entry.
 */
function progressQueryKey(
	journeyType: JourneyType | undefined,
	contentLocale: JourneyContentLocale,
): readonly [
	"journeyProgress",
	JourneyType | "__undefined__",
	JourneyContentLocale,
] {
	return [
		"journeyProgress",
		journeyType ?? "__undefined__",
		contentLocale,
	] as const;
}

function collectJourneyTaskIds(journey: Journey | null): Set<string> {
	const ids = new Set<string>();
	if (!journey) return ids;
	for (const step of journey.steps) {
		for (const task of step.tasks) {
			ids.add(task.id);
		}
	}
	return ids;
}

function dropOrphans<T extends { taskId: string }>(
	items: T[],
	knownTaskIds: Set<string>,
	journey: Journey | null,
): T[] {
	if (!journey) return items;
	return items.filter((item) => knownTaskIds.has(item.taskId));
}

async function fetchMerged(
	journeyType: JourneyType,
	locale: string,
): Promise<MergedPayload> {
	// Run both fetches in parallel; CDN failure is degraded (journey:null)
	// rather than fatal so the consumer can render an empty state.
	const journeyPromise = getJourney(journeyType, locale).catch(
		(error: unknown) => {
			Sentry.addBreadcrumb({
				category: "journey-progress",
				message: `CDN journey fetch failed for ${journeyType}/${locale}`,
				level: "warning",
				data: {
					error: error instanceof Error ? error.message : String(error),
				},
			});
			return null;
		},
	);
	const progressPromise = getJourneyProgress(journeyType);

	const [journeyResult, progress] = await Promise.all([
		journeyPromise,
		progressPromise,
	]);

	const journey = journeyResult?.journey ?? null;
	const knownTaskIds = collectJourneyTaskIds(journey);

	const filteredTaskProgress = dropOrphans(
		progress.taskProgress,
		knownTaskIds,
		journey,
	);
	const filteredCompletedTaskIds = journey
		? progress.completedTaskIds.filter((id) => knownTaskIds.has(id))
		: progress.completedTaskIds;

	// Auto-open the first step for brand-new users who have no step state yet.
	// The backend records the openStep write; the returned stepState is injected
	// directly so the caller doesn't need a second round-trip.
	let stepStates = progress.stepStates;
	if (stepStates.length === 0 && journey && journey.steps.length > 0) {
		const firstStep = journey.steps
			.slice()
			.sort((a, b) => a.order - b.order)[0];
		try {
			const opened = await openStepRequest({
				journeyType,
				stepId: firstStep.id,
			});
			stepStates = [opened];
		} catch (error) {
			Sentry.addBreadcrumb({
				category: "journey-progress",
				message: `Auto-open first step failed for ${journeyType}/${firstStep.id}`,
				level: "warning",
				data: {
					error: error instanceof Error ? error.message : String(error),
				},
			});
		}
	}

	return {
		journey,
		journeyStartedAt: progress.startedAt ?? null,
		stepStates,
		taskProgress: filteredTaskProgress,
		completedTaskIds: filteredCompletedTaskIds,
	};
}

function indexStepStates(states: StepState[]): Map<string, StepState> {
	const map = new Map<string, StepState>();
	for (const s of states) map.set(s.stepId, s);
	return map;
}

function indexTaskProgress(items: TaskProgress[]): Map<string, TaskProgress> {
	const map = new Map<string, TaskProgress>();
	for (const t of items) map.set(t.taskId, t);
	return map;
}

function patchTaskProgress(
	prev: MergedPayload,
	next: TaskProgress,
): MergedPayload {
	const others = prev.taskProgress.filter((t) => t.taskId !== next.taskId);
	const taskProgress = [...others, next];
	const wasCompleted = prev.completedTaskIds.includes(next.taskId);
	let completedTaskIds = prev.completedTaskIds;
	if (next.status === "complete" && !wasCompleted) {
		completedTaskIds = [...prev.completedTaskIds, next.taskId];
	} else if (next.status !== "complete" && wasCompleted) {
		completedTaskIds = prev.completedTaskIds.filter((id) => id !== next.taskId);
	}
	return { ...prev, taskProgress, completedTaskIds };
}

function patchStepState(prev: MergedPayload, next: StepState): MergedPayload {
	const others = prev.stepStates.filter((s) => s.stepId !== next.stepId);
	return { ...prev, stepStates: [...others, next] };
}

function nowIso(): string {
	return new Date().toISOString();
}

function optimisticTaskProgress(input: {
	taskId: string;
	status: TaskProgress["status"];
	progressPercent: number | null;
	completedAt: string | null;
}): TaskProgress {
	return {
		taskId: input.taskId,
		status: input.status,
		progressPercent: input.progressPercent,
		completedAt: input.completedAt,
	};
}

function optimisticStepState(input: {
	stepId: string;
	status: StepState["status"];
	openedAt: string | null;
	closedAt: string | null;
}): StepState {
	return {
		stepId: input.stepId,
		status: input.status,
		openedAt: input.openedAt,
		closedAt: input.closedAt,
	};
}

export function useJourneyProgress(
	journeyType: JourneyType | undefined,
	options?: { enabled?: boolean; locale?: string },
): UseJourneyProgressResult {
	const enabled = (options?.enabled ?? true) && Boolean(journeyType);
	const locale = options?.locale ?? "en";
	const queryClient = useQueryClient();
	const contentLocale = journeyType
		? resolveJourneyContentLocale(journeyType, locale)
		: "en";
	const queryKey = progressQueryKey(journeyType, contentLocale);

	const query = useQuery<MergedPayload, Error>({
		queryKey,
		queryFn: () => {
			if (!journeyType) {
				throw new Error("useJourneyProgress called without a journeyType");
			}
			return fetchMerged(journeyType, locale);
		},
		enabled,
		staleTime: 30 * 1000,
		gcTime: 5 * 60 * 1000,
		refetchOnWindowFocus: false,
	});

	const completeTaskMutation = useMutation<
		TaskProgress,
		Error,
		{ taskId: string; progressPercent?: number },
		{ previous: MergedPayload | undefined }
	>({
		mutationFn: ({ taskId, progressPercent }) => {
			if (!journeyType) {
				throw new Error("completeTask called without a journeyType");
			}
			return completeTaskRequest({ journeyType, taskId, progressPercent });
		},
		onMutate: async ({ taskId, progressPercent }) => {
			await queryClient.cancelQueries({ queryKey });
			const previous = queryClient.getQueryData<MergedPayload>(queryKey);
			if (previous) {
				queryClient.setQueryData<MergedPayload>(
					queryKey,
					patchTaskProgress(
						previous,
						optimisticTaskProgress({
							taskId,
							status: "complete",
							progressPercent: progressPercent ?? 100,
							completedAt: nowIso(),
						}),
					),
				);
			}
			return { previous };
		},
		onError: (_err, _vars, context) => {
			if (context?.previous) {
				queryClient.setQueryData(queryKey, context.previous);
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey });
		},
	});

	const reopenTaskMutation = useMutation<
		TaskProgress,
		Error,
		{ taskId: string },
		{ previous: MergedPayload | undefined }
	>({
		mutationFn: ({ taskId }) => {
			if (!journeyType) {
				throw new Error("reopenTask called without a journeyType");
			}
			return reopenTaskRequest({ journeyType, taskId });
		},
		onMutate: async ({ taskId }) => {
			await queryClient.cancelQueries({ queryKey });
			const previous = queryClient.getQueryData<MergedPayload>(queryKey);
			if (previous) {
				queryClient.setQueryData<MergedPayload>(
					queryKey,
					patchTaskProgress(
						previous,
						optimisticTaskProgress({
							taskId,
							status: "in_progress",
							progressPercent: null,
							completedAt: null,
						}),
					),
				);
			}
			return { previous };
		},
		onError: (_err, _vars, context) => {
			if (context?.previous) {
				queryClient.setQueryData(queryKey, context.previous);
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey });
		},
	});

	const openStepMutation = useMutation<
		StepState,
		Error,
		{ stepId: string },
		{ previous: MergedPayload | undefined }
	>({
		mutationFn: ({ stepId }) => {
			if (!journeyType) {
				throw new Error("openStep called without a journeyType");
			}
			return openStepRequest({ journeyType, stepId });
		},
		onMutate: async ({ stepId }) => {
			await queryClient.cancelQueries({ queryKey });
			const previous = queryClient.getQueryData<MergedPayload>(queryKey);
			if (previous) {
				queryClient.setQueryData<MergedPayload>(
					queryKey,
					patchStepState(
						previous,
						optimisticStepState({
							stepId,
							status: "in_progress",
							openedAt: nowIso(),
							closedAt: null,
						}),
					),
				);
			}
			return { previous };
		},
		onError: (_err, _vars, context) => {
			if (context?.previous) {
				queryClient.setQueryData(queryKey, context.previous);
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey });
		},
	});

	const closeStepMutation = useMutation<
		StepState,
		Error,
		{ stepId: string },
		{ previous: MergedPayload | undefined }
	>({
		mutationFn: ({ stepId }) => {
			if (!journeyType) {
				throw new Error("closeStep called without a journeyType");
			}
			return closeStepRequest({ journeyType, stepId });
		},
		onMutate: async ({ stepId }) => {
			await queryClient.cancelQueries({ queryKey });
			const previous = queryClient.getQueryData<MergedPayload>(queryKey);
			if (previous) {
				queryClient.setQueryData<MergedPayload>(
					queryKey,
					patchStepState(
						previous,
						optimisticStepState({
							stepId,
							status: "complete",
							openedAt:
								previous.stepStates.find((s) => s.stepId === stepId)
									?.openedAt ?? null,
							closedAt: nowIso(),
						}),
					),
				);
			}
			return { previous };
		},
		onError: (_err, _vars, context) => {
			if (context?.previous) {
				queryClient.setQueryData(queryKey, context.previous);
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey });
		},
	});

	const data = query.data;
	const stepStatesMap = useMemo(
		() => indexStepStates(data?.stepStates ?? []),
		[data?.stepStates],
	);
	const taskProgressMap = useMemo(
		() => indexTaskProgress(data?.taskProgress ?? []),
		[data?.taskProgress],
	);
	const completedTaskIdsSet = useMemo(
		() => new Set(data?.completedTaskIds ?? []),
		[data?.completedTaskIds],
	);

	return {
		journey: data?.journey ?? null,
		journeyStartedAt: data?.journeyStartedAt ?? null,
		stepStates: stepStatesMap,
		taskProgress: taskProgressMap,
		completedTaskIds: completedTaskIdsSet,
		isLoading: query.isLoading,
		error: query.error ?? null,
		mutations: {
			completeTask: completeTaskMutation,
			reopenTask: reopenTaskMutation,
			openStep: openStepMutation,
			closeStep: closeStepMutation,
		},
	};
}
